import React, { useState, useEffect } from "react";
import "./IntegrationModals.css";
import { FiX, FiCopy, FiCheck, FiHelpCircle, FiExternalLink, FiBook } from "react-icons/fi";
import { FaGlobe } from "react-icons/fa";
import { api } from "../../../services/api";

const SiteIntegration = ({
  isOpen,
  onClose,
  providerKey,
  integrationId,
  env = "production",
  onSave,
  credentials,
}) => {
  const [formData, setFormData] = useState({
    webhook_url: "",
    webhook_secret: "",
    site_url: "",
    revalidate_on_publish: true,
    revalidate_on_update: true,
    revalidate_on_delete: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [copied, setCopied] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [loadingApiKey, setLoadingApiKey] = useState(false);

  useEffect(() => {
    if (credentials?.summary) {
      setFormData({
        webhook_url: credentials.summary.webhook_url || "",
        webhook_secret: "", // Não mostrar o secret existente por segurança
        site_url: credentials.summary.site_url || "",
        revalidate_on_publish: credentials.summary.revalidate_on_publish ?? true,
        revalidate_on_update: credentials.summary.revalidate_on_update ?? true,
        revalidate_on_delete: credentials.summary.revalidate_on_delete ?? true,
      });
    }
  }, [credentials]);

  // Buscar API Key do CMS
  useEffect(() => {
    const fetchApiKey = async () => {
      setLoadingApiKey(true);
      try {
        const response = await api.get("/api/cms/api-key");
        setApiKey(response.data.api_key);
      } catch (err) {
        console.log("API Key não disponível ainda");
      } finally {
        setLoadingApiKey(false);
      }
    };

    if (isOpen) {
      fetchApiKey();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const generateSecret = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let secret = "";
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, webhook_secret: secret }));
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validações
      if (!formData.webhook_url) {
        throw new Error("URL do webhook é obrigatória");
      }

      if (!formData.webhook_url.startsWith("http://") && !formData.webhook_url.startsWith("https://")) {
        throw new Error("URL deve começar com http:// ou https://");
      }

      // Se não tem integrationId, a secret é obrigatória
      // Se já tem, pode atualizar sem mudar a secret
      if (!integrationId && !formData.webhook_secret) {
        throw new Error("Secret do webhook é obrigatório");
      }

      if (formData.webhook_secret && formData.webhook_secret.length < 8) {
        throw new Error("Secret deve ter pelo menos 8 caracteres");
      }

      // Construir payload
      const payload = {
        credentials: {
          webhook_url: formData.webhook_url.trim(),
          site_url: formData.site_url?.trim() || null,
          revalidate_on_publish: formData.revalidate_on_publish,
          revalidate_on_update: formData.revalidate_on_update,
          revalidate_on_delete: formData.revalidate_on_delete,
        },
      };

      // Só incluir secret se foi preenchido
      if (formData.webhook_secret) {
        payload.credentials.webhook_secret = formData.webhook_secret;
      }

      if (integrationId) {
        // Atualizar integração existente
        await api.put(`/api/integrations/${integrationId}`, payload);
      } else {
        // Criar nova integração
        payload.env = env;
        await api.post(`/api/integrations/${providerKey}/connect`, payload);
      }

      onSave && onSave(providerKey, formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Erro ao salvar integração");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="integration-modal-overlay">
      <div className="integration-modal integration-modal-large">
        <div className="integration-modal-header">
          <div className="integration-modal-title-row">
            <FaGlobe className="integration-modal-icon" />
            <h2>Integração com Website</h2>
          </div>
          <button className="integration-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="integration-modal-tabs">
          <button
            className={`integration-tab ${!showDocs ? "active" : ""}`}
            onClick={() => setShowDocs(false)}
          >
            Configuração
          </button>
          <button
            className={`integration-tab ${showDocs ? "active" : ""}`}
            onClick={() => setShowDocs(true)}
          >
            <FiBook />
            Documentação
          </button>
        </div>

        {showDocs ? (
          <div className="integration-modal-docs">
            <div className="docs-section">
              <h3>Como funciona a integração?</h3>
              <p>
                Quando você publica, atualiza ou remove conteúdo no CMS, o FastTeam
                envia automaticamente um webhook para o seu site, permitindo que ele
                revalide as páginas afetadas em tempo real.
              </p>
            </div>

            <div className="docs-section">
              <h3>1. Configurar variáveis de ambiente no seu site</h3>
              <div className="docs-code">
                <pre>{`# .env.local
CMS_API_URL=https://api.fastteam.com.br
CMS_API_KEY=${apiKey || "sua_api_key_aqui"}
CMS_WEBHOOK_SECRET=${formData.webhook_secret || "seu_secret_aqui"}
NEXT_PUBLIC_SITE_URL=https://seusite.com`}</pre>
                <button
                  className="docs-copy-btn"
                  onClick={() => copyToClipboard(`CMS_API_URL=https://api.fastteam.com.br
CMS_API_KEY=${apiKey || "sua_api_key_aqui"}
CMS_WEBHOOK_SECRET=${formData.webhook_secret || "seu_secret_aqui"}
NEXT_PUBLIC_SITE_URL=https://seusite.com`, "env")}
                >
                  {copied === "env" ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            </div>

            <div className="docs-section">
              <h3>2. Criar endpoint de revalidação (Next.js)</h3>
              <p>Crie o arquivo <code>app/api/revalidate/route.ts</code>:</p>
              <div className="docs-code docs-code-large">
                <pre>{`import { revalidatePath, revalidateTag } from 'next/cache';
import { NextRequest, NextResponse } from 'next/server';

export async function POST(request: NextRequest) {
  // Verificar secret
  const secret = request.headers.get('X-Webhook-Secret');
  if (secret !== process.env.CMS_WEBHOOK_SECRET) {
    return NextResponse.json({ error: 'Unauthorized' }, { status: 401 });
  }

  const payload = await request.json();
  const { event, content, paths_to_revalidate, tags_to_revalidate } = payload;

  // Revalidar paths
  for (const path of paths_to_revalidate || []) {
    revalidatePath(path);
  }

  // Revalidar tags
  for (const tag of tags_to_revalidate || []) {
    revalidateTag(tag);
  }

  return NextResponse.json({ success: true, event });
}`}</pre>
                <button
                  className="docs-copy-btn"
                  onClick={() => copyToClipboard(`import { revalidatePath, revalidateTag } from 'next/cache';
import { NextRequest, NextResponse } from 'next/server';

export async function POST(request: NextRequest) {
  const secret = request.headers.get('X-Webhook-Secret');
  if (secret !== process.env.CMS_WEBHOOK_SECRET) {
    return NextResponse.json({ error: 'Unauthorized' }, { status: 401 });
  }

  const payload = await request.json();
  const { event, content, paths_to_revalidate, tags_to_revalidate } = payload;

  for (const path of paths_to_revalidate || []) {
    revalidatePath(path);
  }

  for (const tag of tags_to_revalidate || []) {
    revalidateTag(tag);
  }

  return NextResponse.json({ success: true, event });
}`, "revalidate")}
                >
                  {copied === "revalidate" ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            </div>

            <div className="docs-section">
              <h3>3. Buscar conteúdo da API pública</h3>
              <div className="docs-code">
                <pre>{`// Buscar posts
const response = await fetch(
  \`\${process.env.CMS_API_URL}/api/v1/cms/posts\`,
  {
    headers: { 'X-API-Key': process.env.CMS_API_KEY },
    next: { tags: ['posts'] }
  }
);`}</pre>
              </div>
            </div>

            <div className="docs-section">
              <h3>Eventos de Webhook</h3>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>content.published</code></td>
                    <td>Quando página ou post é publicado</td>
                  </tr>
                  <tr>
                    <td><code>content.updated</code></td>
                    <td>Quando conteúdo publicado é atualizado</td>
                  </tr>
                  <tr>
                    <td><code>content.deleted</code></td>
                    <td>Quando conteúdo é removido</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="docs-section">
              <h3>Endpoints da API Pública</h3>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>GET /api/v1/cms/pages</code></td>
                    <td>Lista páginas publicadas</td>
                  </tr>
                  <tr>
                    <td><code>GET /api/v1/cms/posts</code></td>
                    <td>Lista posts publicados</td>
                  </tr>
                  <tr>
                    <td><code>GET /api/v1/cms/categories</code></td>
                    <td>Lista categorias</td>
                  </tr>
                  <tr>
                    <td><code>GET /api/v1/cms/sitemap</code></td>
                    <td>Dados para sitemap.xml</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="docs-actions">
              <a
                href="https://docs.fastteam.com.br/cms/integracao-site"
                target="_blank"
                rel="noopener noreferrer"
                className="docs-link"
              >
                <FiExternalLink />
                Ver documentação completa
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="integration-modal-form">
            {error && <div className="integration-error">{error}</div>}

            {/* API Key Info */}
            {apiKey && (
              <div className="integration-info-box">
                <div className="info-box-header">
                  <FiHelpCircle />
                  <span>Sua API Key do CMS</span>
                </div>
                <div className="info-box-content">
                  <code className="api-key-display">{apiKey}</code>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => copyToClipboard(apiKey, "apikey")}
                  >
                    {copied === "apikey" ? <FiCheck /> : <FiCopy />}
                  </button>
                </div>
                <p className="info-box-hint">
                  Use esta chave no seu site para acessar a API pública do CMS
                </p>
              </div>
            )}

            <div className="integration-form-group">
              <label htmlFor="webhook_url">
                URL do Webhook <span className="required">*</span>
              </label>
              <input
                type="url"
                id="webhook_url"
                name="webhook_url"
                value={formData.webhook_url}
                onChange={handleChange}
                placeholder="https://meusite.com/api/revalidate"
                required
              />
              <span className="field-hint">
                Endpoint do seu site que receberá as notificações
              </span>
            </div>

            <div className="integration-form-group">
              <label htmlFor="webhook_secret">
                Secret do Webhook {!integrationId && <span className="required">*</span>}
              </label>
              <div className="input-with-button">
                <input
                  type="text"
                  id="webhook_secret"
                  name="webhook_secret"
                  value={formData.webhook_secret}
                  onChange={handleChange}
                  placeholder={integrationId ? "Deixe vazio para manter o atual" : "meu_secret_seguro"}
                  required={!integrationId}
                  minLength={8}
                />
                <button
                  type="button"
                  className="generate-btn"
                  onClick={generateSecret}
                >
                  Gerar
                </button>
              </div>
              <span className="field-hint">
                Chave secreta para autenticar os webhooks (mínimo 8 caracteres)
              </span>
            </div>

            <div className="integration-form-group">
              <label htmlFor="site_url">URL do Site (opcional)</label>
              <input
                type="url"
                id="site_url"
                name="site_url"
                value={formData.site_url}
                onChange={handleChange}
                placeholder="https://meusite.com"
              />
              <span className="field-hint">
                URL pública do seu site (para preview de conteúdo)
              </span>
            </div>

            <div className="integration-form-section">
              <h4>Quando enviar webhooks?</h4>

              <div className="integration-checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="revalidate_on_publish"
                    checked={formData.revalidate_on_publish}
                    onChange={handleChange}
                  />
                  <span className="checkbox-text">
                    <strong>Ao publicar conteúdo</strong>
                    <small>Notificar quando páginas ou posts forem publicados</small>
                  </span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="revalidate_on_update"
                    checked={formData.revalidate_on_update}
                    onChange={handleChange}
                  />
                  <span className="checkbox-text">
                    <strong>Ao atualizar conteúdo</strong>
                    <small>Notificar quando conteúdo publicado for editado</small>
                  </span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="revalidate_on_delete"
                    checked={formData.revalidate_on_delete}
                    onChange={handleChange}
                  />
                  <span className="checkbox-text">
                    <strong>Ao remover conteúdo</strong>
                    <small>Notificar quando conteúdo for deletado ou despublicado</small>
                  </span>
                </label>
              </div>
            </div>

            <div className="integration-modal-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? "Salvando..." : integrationId ? "Salvar Alterações" : "Conectar Site"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SiteIntegration;
